import type { TokenBucket } from '../../../api/agent-miner-client/agent-miner-client.type';

/**
 * Token measure charted by the Token-usage view. Each value maps to a numeric
 * key of {@link TokenBucket} (or a derived total), selected via the sidebar
 * dropdown and held in the `measure` URL param. Cache read and cache write are
 * kept separate: they have opposite cost implications (writes cost a premium
 * over input, reads are heavily discounted), so summing them hides the signal.
 */
type UsageMeasure = 'total' | 'input' | 'output' | 'cache_read' | 'cache_write';

/**
 * Representation of the Token-usage view, toggled from the chart header (the
 * `usage_view` URL param): a line chart, grouped bar chart, or the token
 * breakdown table.
 */
type UsageView = 'line' | 'bar' | 'table';

/** The default measure when the `measure` URL param is absent or invalid. */
const DEFAULT_USAGE_MEASURE: UsageMeasure = 'total';

/** Known measure values, used to validate a hand-edited URL param. */
const USAGE_MEASURE_VALUES = new Set<string>(['total', 'input', 'output', 'cache_read', 'cache_write']);

/**
 * Projects the single number a given measure charts out of a token bucket.
 * `cache_write` maps to the bucket's cache-creation half.
 *
 * @param {TokenBucket} bucket - A per-period token bucket from the payload
 * @param {UsageMeasure} measure - The selected measure
 * @returns {number} The measure's value for this bucket
 */
const measureValue = (bucket: TokenBucket, measure: UsageMeasure): number => {
  switch (measure) {
    case 'input':
      return bucket.input_tokens;
    case 'output':
      return bucket.output_tokens;
    case 'cache_read':
      return bucket.cache_read_tokens;
    case 'cache_write':
      return bucket.cache_creation_tokens;
    default:
      return bucket.total_tokens;
  }
};

export { DEFAULT_USAGE_MEASURE, USAGE_MEASURE_VALUES, measureValue };
export type { UsageMeasure, UsageView };
